import {getParamId, getParamId2, tempUnit} from '../utils/utils'
import {secsToDisplayDate} from '../../components/utils/utils'
import React from "react";
/*
  getHR16InfoText - returns a short info string for the HydroRoot16 "Info" column.
  Only called when isHR16 is true.

  Irrigation types and what they show:
    Scheduled:        next start time + runs; during slot on-window hide time only (runs stay visible)
    Accumulated Light:"6.8 / 12.5 Mol/m² @ 73.2°F" + run count
    Cycle:            "Ends in 0:02:15 (3:27pm)" (ON), "Next in 0:04:32 (3:25pm)" (cycling), "Starts in 5:30:00 (6:00pm)" (outside window)
    VPD:              "5.2 / 14 kPa-Hr" + run count
    Soil Trigger:     "45 / 23 VWC %" + run count

  params: { ch, chType, zone, zInd }
*/

var ccd = "configuration_channel_data"
var scd = "snapshot_channel_data"
var sn  = "snapshots"

// Backend uses 10000 for invalid/unavailable sensor readings (see ngui-node mqtt.js undefinedSensorValue)
var UNDEFINED_SENSOR = 10000

// Pearl snapshots.currentTime is Unix seconds (see C18Host00 getZoneTimeDisplay). It only updates
// when MQTT/snapshot data arrives, so between pushes the value is stale. Extrapolate with wall
// clock so scheduled/cycle UI (in-run window, next start) tracks real time between updates.
var _ctrlUnixAnchor = {}

var getControllerUnixExtrapolated = (zone, zInd) => {
  let pid = getParamId(sn, "currentTime")
  let raw = +((zone[240] || {})[pid] || 0)
  if (!raw) return raw
  let key = zInd != null ? String(zInd) : "_"
  let prev = _ctrlUnixAnchor[key]
  if (!prev || prev.raw !== raw) {
    _ctrlUnixAnchor[key] = { raw, recvMs: Date.now() }
    return raw
  }
  return prev.raw + (Date.now() - prev.recvMs) / 1000
}

// Seconds since midnight (0..86400), extrapolated between snapshot updates when zInd is provided.
var getDaySecsFromZone = (zone, zInd) => {
  let u = (zInd != null)
    ? getControllerUnixExtrapolated(zone, zInd)
    : +((zone[240] || {})[getParamId(sn, "currentTime")] || 0)
  let d = u % 86400
  return d < 0 ? d + 86400 : d
}

var getNowMinutesFromZone = (zone, zInd) => {
  return Math.floor(getDaySecsFromZone(zone, zInd) / 60)
}

// find the next scheduled start time (returns minutes, or "" if done)
var getNextStartTime = (ch, now) => {
  let times = []
  for (let t = 0; t < 6; t++) {
    let start = +ch[getParamId(ccd, `start_time_${t + 1}`)]
    if (start < 1440) times.push(start)
  }
  if (!times.length) return { next: "", total: 0 }
  times.sort((a, b) => a - b)
  times.push(1440 + times[0]) // wrap to next day
  let next = ""
  for (let t = 0; t < times.length; t++) {
    if (+times[t] > now) { next = times[t]; break }
  }
  return { next, total: times.length - 1 } // -1 because we added wrap
}

// format minutes to display time like "3:25pm"
var minsToTime = (mins) => {
  return secsToDisplayDate(60 * mins, "h:mm AP")
}

// format seconds to hh:mm:ss countdown string
var secsToCountdown = (secs) => {
  if (secs <= 0) return "0:00:00"
  let h = Math.floor(secs / 3600)
  let m = Math.floor((secs % 3600) / 60)
  let s = secs % 60
  return `${h}:${m < 10 ? "0" : ""}${m}:${s < 10 ? "0" : ""}${s}`
}

// format seconds to compact h:mm:ss or m:ss (omits hours when zero)
var secsToHms = (secs) => {
  if (secs <= 0) return "0:00"
  let h = Math.floor(secs / 3600)
  let m = Math.floor((secs % 3600) / 60)
  let s = secs % 60
  let pad = (n) => (n < 10 ? "0" : "") + n
  if (h) return `${h}:${pad(m)}:${pad(s)}`
  return `${m}:${pad(s)}`
}

// return structured countdown object for UI timer rendering (whole seconds only)
var countdownObj = (secs) => {
  let t = Math.max(0, Math.floor(+secs || 0))
  if (t <= 0) return { h: 0, m: 0, s: 0 }
  return {
    h: Math.floor(t / 3600),
    m: Math.floor((t % 3600) / 60),
    s: t % 60,
  }
}

// ---- Info text per irrigation type ----

// Today's irrigation run count from controller snapshot (same as legacy HydroRoot "Runs Today").
var getIrrigationRunCount = (ch) => {
  let n = +(ch[getParamId(scd, "activeCount")] || 0)
  if (!Number.isFinite(n) || n < 0) return 0
  return Math.floor(n)
}

var boxedValues = (values, unit, runs) => ({
  type: "valueBoxes",
  values,
  unit,
  runs,
})

// Scheduled irrigation: show next start time + countdown. runs = activeCount (completed today).
// HydroRoot only: in a slot's on window, hide next start time but still show runs count.
var infoScheduled = (ch, zone, zInd) => {
  let daySecs = getDaySecsFromZone(zone, zInd)
  let runs = getIrrigationRunCount(ch)
  for (let t = 0; t < 6; t++) {
    let startMin = +ch[getParamId(ccd, `start_time_${t + 1}`)]
    if (startMin >= 1440) continue
    let durSec = +ch[getParamId(ccd, `duration_${t + 1}_irr`)] || 0
    if (durSec <= 0) continue
    let startSec = startMin * 60
    let endAbs = startSec + durSec
    let inRun = (endAbs <= 86400)
      ? (daySecs >= startSec && daySecs < endAbs)
      : (daySecs >= startSec || daySecs < (endAbs % 86400))
    if (inRun) return { type: "scheduledInRun", runs }
  }
  let now = getNowMinutesFromZone(zone, zInd)
  let { next, total } = getNextStartTime(ch, now)
  if (!total) return "Not set"
  if (next !== "") {
    let secsUntil = (next - now) * 60
    return {
      type: "countdown",
      label: "Next in",
      totalSecs: secsUntil,
      countdown: countdownObj(secsUntil),
      suffix: minsToTime(next),
      runs,
    }
  }
  return {
    type: "scheduledDone",
    runs,
  }
}

// Accumulated light / threshold. Units: mol/m².
// accumulatedLight is per-channel snapshot data (snapshot_channel_data), not zone-level.
// Controller stores accumulatedLight in millionths (same as dailyLightIntegral/analogDliAccum in C18utils).
// Threshold is temperature-interpolated between setting_1 and setting_2 (same formula as C18ZonesHydroRoot).
var infoAccumulatedLight = (ch, zone, zInd) => {
  let accumLight = +(ch[getParamId(scd, "accumulatedLight")] || 0)
  let dli = Math.round((accumLight / 1e6) * 10) / 10
  let inTemp = +((zone[240] || {})[getParamId(sn, "inTemperature")] || 0)
  let temp1 = +(ch[getParamId(ccd, "setting_1_threshold_temp")] || 0)
  let lite1 = +(ch[getParamId(ccd, "setting_1_threshold_light")] || 0)
  let temp2 = +(ch[getParamId(ccd, "setting_2_threshold_temp")] || 0)
  let lite2 = +(ch[getParamId(ccd, "setting_2_threshold_light")] || 0)
  let lo = Math.min(temp1, temp2)
  let hi = Math.max(temp1, temp2)
  let clamped = Math.max(Math.min(inTemp, hi), lo)
  let thr = (temp2 !== temp1)
    ? Math.round((lite1 + (lite2 - lite1) * (clamped - temp1) / (temp2 - temp1)) * 10) / 10
    : Math.round(lite1 * 10) / 10
  let tu = tempUnit(zInd)
  let tempStr
  if (inTemp >= UNDEFINED_SENSOR) {
    tempStr = "\u2014"
  } else {
   tempStr = `${inTemp}${tu.t}`
  }
  return boxedValues([`${dli}`, `${thr}`], `Mol/m\u00B2`, getIrrigationRunCount(ch))
}

// Cycle irrigation: compute ON/OFF state and countdown from config + controller time.
// Mirrors Pearl firmware IrrDevice.c IRRMODE_CYCL logic:
//   cyclePosSec = ((nowMins - startWindow) % period) * 60 + nowSecs
//   adjustedOnTime = interpolate(dur1, dur2, temp1, temp2, currentTemp)
//   ON when cyclePosSec < adjustedOnTime, OFF otherwise
//
// Returns type "cycleCountdown" with onDuration + periodSec so the renderer
// can advance the cycle position locally and handle ON→OFF transitions
// without waiting for the next snapshot update.
var infoCycle = (ch, zone, zInd) => {
  let runs = getIrrigationRunCount(ch)
  let period = +(ch[getParamId(ccd, "off_duration")] || 0)
  let startMin = +(ch[getParamId(ccd, "start")] || 0)
  let endMin = +(ch[getParamId(ccd, "end")] || 0)

  if (period <= 0) return "---"

  // Integer second-of-day so cycle position matches firmware (extrapolated clock can be fractional).
  let wholeDaySecs = Math.floor(getDaySecsFromZone(zone, zInd))
  let nowMins = Math.floor(wholeDaySecs / 60)
  let nowSecs = wholeDaySecs % 60

  let inWindow = (startMin < endMin)
    ? (nowMins >= startMin && nowMins < endMin)
    : (nowMins >= startMin || nowMins < endMin)

  if (!inWindow) {
    let secsUntil
    if (nowMins < startMin) {
      secsUntil = (startMin - nowMins) * 60 - nowSecs
    } else {
      secsUntil = (1440 - nowMins + startMin) * 60 - nowSecs
    }
    if (secsUntil <= 0) secsUntil += 86400
    return {
      type: "countdown",
      label: "Starts in",
      totalSecs: secsUntil,
      countdown: countdownObj(secsUntil),
      runs,
    }
  }

  let dur1 = +(ch[getParamId(ccd, "duration_1_irr")] || ch[getParamId(ccd, "on_duration")] || 0)
  let dur2 = +(ch[getParamId(ccd, "duration_2_irr")] || 0)
  let temp1 = +(ch[getParamId(ccd, "cycl_temp_1_irr")] || 0)
  let temp2 = +(ch[getParamId(ccd, "cycl_temp_2_irr")] || 0)
  let inTemp = +((zone[240] || {})[getParamId(sn, "inTemperature")] || 0)

  let adjustedOnTime
  if (temp1 === temp2 || dur2 === 0) {
    adjustedOnTime = dur1
  } else {
    let tMin = Math.min(temp1, temp2)
    let tMax = Math.max(temp1, temp2)
    let clamped = Math.max(Math.min(inTemp, tMax), tMin)
    let slope = (dur2 - dur1) / (temp2 - temp1)
    adjustedOnTime = Math.max(0, Math.round(dur1 + slope * (clamped - temp1)))
  }

  let periodSec = period * 60
  let wbPct = +((zone[240] || {})[getParamId2(1900, "pearl_config", "water_budget_pct")]) || 100
  let wbEnd = +((zone[240] || {})[getParamId2(1900, "pearl_config", "water_budget_days")]) || 0
  let wbExpired = wbEnd > 1000000000 && wbEnd <= Math.floor(Date.now() / 1000)
  if (wbPct !== 100 && wbPct > 0 && !wbExpired) {
    adjustedOnTime = Math.min(Math.round(adjustedOnTime * wbPct / 100), periodSec)
  }
  let elapsedMin = ((nowMins - startMin) + 1440) % 1440
  let cyclePosMin = elapsedMin % period
  let cyclePosSec = cyclePosMin * 60 + nowSecs

  return {
    type: "cycleCountdown",
    onDuration: adjustedOnTime,
    periodSec,
    cyclePosSec,
    runs,
  }
}

// Accumulated VPD / threshold. Units: kPa·hr.
// accvpd is per-channel snapshot data (snapshot_channel_data).
var infoVPD = (ch) => {
  let accvpd = +(ch[getParamId(scd, "accvpd")] || 0)
  let threshold = +(ch[getParamId(ccd, "vpdacc_threshold")] || 0)
  let av = Math.round(accvpd * 10) / 10
  let th = Math.round(threshold * 10) / 10
  return boxedValues([`${av}`, `${th}`], "kPa-Hr", getIrrigationRunCount(ch))
}

// Soil trigger: current soil % / threshold %.
// Backend sends 10000 for invalid/unavailable sensor readings; show "—" instead.
var infoSoilTrigger = (ch, zone) => {
  let threshold = +(ch[getParamId(ccd, "soil_moisture_threshold")] || 0)
  let sensorIdx = +(ch[getParamId(ccd, "soil_moisture_input")] || 0)
  let soilVal = +((zone[240] || {})[getParamId(sn, `soilMoisture${sensorIdx + 1}`)] || 0)
  let th = Math.round(threshold * 10) / 10
  let currentStr = (soilVal >= UNDEFINED_SENSOR) ? "—" : `${Math.round(soilVal * 10) / 10}`
  return boxedValues([currentStr, `${th}`], "VWC %", getIrrigationRunCount(ch))
}

var infoSupplyPump = (ch) => {
  let relay = +(ch[getParamId("snapshot_channels", "relay")] || 0)
  return relay ? "On when demanded" : "Off"
}

var infoVariableOut = (ch) => {
  let pos = +(ch[getParamId("snapshot_channels", "position")] ?? ch[getParamId("snapshot_channels", "analogOutput")] ?? 0)
  if (pos > 0 && pos <= 100) return `${pos}%`
  let relay = +(ch[getParamId("snapshot_channels", "relay")] || 0)
  return relay ? "On" : "Off"
}

// ---- Main export ----

var getHR16InfoText = ({ch, chType, zone, zInd}) => {
  switch (chType) {
    case "channel_Irrigation_Scheduled":
    case "channel_Irrigation_Scheduled_1900":
      return infoScheduled(ch, zone, zInd)
    case "channel_Irrigation_Accumulated_Light":
      return infoAccumulatedLight(ch, zone, zInd)
    case "channel_Irrigation_Cycle":
      return infoCycle(ch, zone, zInd)
    case "channel_Irrigation_VPD":
      return infoVPD(ch)
    case "channel_Irrigation_Soil_Trigger":
      return infoSoilTrigger(ch, zone)
    case "channel_Supply_Pump":
      return infoSupplyPump(ch)
    case "channel_Variable_Out":
    case "channel_Variable_Out_1900":
      return infoVariableOut(ch)
    default:
      return "---"
  }
}

var processHR16Countdown = (infoText, cdEpochs, chIdx) => {
  if (!infoText) return infoText

  // Scheduled in-run replaces countdown; clear stale epoch so after the run ends we do not
  // reuse the old epoch when totalSecs matches the pre-run countdown (same next slot).
  if (infoText.type === "scheduledInRun" || infoText.type === "scheduledDone") {
    delete cdEpochs[chIdx]
    return infoText
  }

  // Local smoothing: snapshot gives cyclePosSec/onDuration/periodSec; we tick remaining by wall clock.
  // Integer seconds in `countdown` / `totalSecs` are display-only; ON↔OFF transitions still use `remaining` before floor.
  if (infoText.type === "cycleCountdown") {
    let info = infoText
    let pos = info.cyclePosSec
    let snapshotLabel, snapshotRemaining
    if (pos < info.onDuration) {
      snapshotLabel = "ON"
      snapshotRemaining = info.onDuration - pos
    } else {
      snapshotLabel = "OFF"
      snapshotRemaining = info.periodSec - pos
    }
    snapshotRemaining = Math.max(0, snapshotRemaining)
    let cd = cdEpochs[chIdx]
    if (!cd || cd.totalSecs !== snapshotRemaining || cd.cycleLabel !== snapshotLabel) {
      cdEpochs[chIdx] = {totalSecs: snapshotRemaining, epoch: Date.now(),
        cycleLabel: snapshotLabel, onDuration: info.onDuration, periodSec: info.periodSec}
      cd = cdEpochs[chIdx]
    }
    let elapsed = Math.floor((Date.now() - cd.epoch) / 1000)
    let remaining = cd.totalSecs - elapsed
    let label = cd.cycleLabel
    if (remaining <= 0 && cd.cycleLabel === "ON") {
      label = "OFF"
      let offDuration = cd.periodSec - cd.onDuration
      remaining = offDuration + remaining
      if (remaining < 0) remaining = 0
    } else if (remaining <= 0 && cd.cycleLabel === "OFF") {
      label = "ON"
      remaining = cd.onDuration + remaining
      if (remaining < 0) remaining = 0
    }
    remaining = Math.max(0, remaining)
    let rs = Math.floor(remaining)
    return {
      type: "countdown",
      label,
      totalSecs: rs,
      countdown: {
        h: Math.floor(rs / 3600),
        m: Math.floor((rs % 3600) / 60),
        s: rs % 60,
      },
      runs: info.runs,
    }
  }

  if (infoText.type === "countdown") {
    let cd = cdEpochs[chIdx]
    if (!cd || cd.totalSecs !== infoText.totalSecs) {
      cdEpochs[chIdx] = {totalSecs: infoText.totalSecs, epoch: Date.now()}
      cd = cdEpochs[chIdx]
    }
    let elapsed = Math.floor((Date.now() - cd.epoch) / 1000)
    let remaining = Math.max(0, cd.totalSecs - elapsed)
    let rs = Math.floor(remaining)
    infoText.countdown = {
      h: Math.floor(rs / 3600),
      m: Math.floor((rs % 3600) / 60),
      s: rs % 60,
    }
  }

  return infoText
}

export {getHR16InfoText, processHR16Countdown}
